import { AlertCircle, Building, Bus, Calendar, CheckCircle, Clock, Coins, Eye, GraduationCap, Heart, Leaf, MapPin, TrendingUp } from "lucide-react";
import { FC } from "react";
import { Badge, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { envConfig, getProjectEvaluationLink } from "../../../../utils/config";
import GateStepper from "../../project/components/GateStepper";
import { IProject } from "../../project";
import { Link, useLocation } from "react-router-dom";
type Props = {
  project: IProject,
}

const gradients = {
  topBar: "linear-gradient(90deg, #1e4780 0%, #4a8fe0 50%, #5a9ff0 100%)",
  badge: "linear-gradient(135deg, #3c72e7 0%, #4a8fe0 100%)",
  iconBg: "linear-gradient(135deg, #3c72e7 0%, #2b4d75 100%)",
  lightBlue: "linear-gradient(145deg, #e8f1fe 0%, #d6e4ff 100%)",
  lightSuccess: "linear-gradient(145deg, #e8f8f0 0%, #d4f7df 100%)",
  lightWarning: "linear-gradient(145deg, #fff8e6 0%, #ffe8b3 100%)",
  lightDanger: "linear-gradient(145deg, #fdecec 0%, #ffd6d6 100%)",
  bgInfo: "linear-gradient(135deg, #3498db 0%, #5dade2 100%)",
  bgSuccess: "linear-gradient(135deg, #2ecc71 0%, #40d87e 100%)",
  success: "#229150",
  warning: "#f39c12",
  danger: "#aa2517",
  info: "#3498db",
};

const InfoItem = ({ icon, value }: { icon: string, value: string | number }) => (
  <div className="d-flex align-items-center gap-2 flex-grow-1 w-50" style={{ minWidth: 0 }}>
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{
        background: gradients.lightBlue,
        width: "30px",
        height: "30px"
      }}
    >
      <i className={icon} style={{ color: "#3a7bd5", fontSize: "1.2rem" }}></i>
    </div>
    <div className="d-flex flex-column min-w-0">
      <span className="fw-semibold  fs-3">
        {value}
      </span>
    </div>
  </div>
)

const EnhancedProjectCard: FC<Props> = ({ project }) => {
  const stages = project.stages || [];
  const location = useLocation();

  return (
    <div className={`h-100 border-0 rounded-4 overflow-hidden transition-all card shadow-sm`}>
      <div className="bg-gray-300">
        <div
          style={{
            height: "6px",
            background: gradients.topBar,
            width: `${project.progress}%`
          }}
        />
      </div>
      <div className="card-body p-4 d-flex flex-column justify-content-between">
        <div>
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`tooltip-project-name`}> {project.name} </Tooltip>}
            >
              <h5 className="card-title mb-0 text-dark project-name">
                <Link to={`/manage-project/${project.project_id}/${location?.search}`} state={{ from: location.state?.from || location.pathname, }} className="text-decoration-none text-dark"> {project.name}</Link>
              </h5>
            </OverlayTrigger>
          </div>
          <div className="d-flex align-items-center justify-content-start gap-3 w-100 mb-3">
            {project.executiveSummary !== "#" ? <Link to={`${project.executiveSummary}`} target={'_blank'}>
              <Badge
                className="px-3 py-2 fw-semibold border-0 shadow-sm flex-shrink-0 text-white"
                pill
                style={{
                  background: gradients.bgInfo,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <AlertCircle className="me-2" size={15} />
                Draft Prospectus
              </Badge>
            </Link> : <Badge
              className="px-3 py-2 fw-semibold border-0 shadow-sm flex-shrink-0"
              pill
              style={{
                background: gradients.lightBlue,
                color: gradients.info,
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              <AlertCircle className="me-2" size={15} />
              Draft Prospectus
            </Badge>
            }
            {project.elevatorPitch !== "#" ? <Link to={`${project.elevatorPitch}`} target={'_blank'}>
              <Badge
                className="px-3 py-2 fw-semibold border-0 shadow-sm flex-shrink-0 text-white"
                pill
                style={{
                  background: gradients.bgSuccess,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <Building className="me-2" size={15} />
                Draft Elevator Pitch
              </Badge>
            </Link> : <Badge
              className="px-3 py-2 fw-semibold border-0 shadow-sm flex-shrink-0"
              pill
              style={{
                background: gradients.lightSuccess,
                color: gradients.success,
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              <Building className="me-2" size={15} />
              Draft Elevator Pitch
            </Badge>
            }
          </div>
          <div className="d-flex align-items-center justify-content-between gap-2 w-100 mb-3">
            {project.district_name && <div
              className="text-dark badge border-0 rounded-pill px-3 py-2 fw-medium d-flex align-items-center fw-semibold"
              style={{
                background: gradients.lightBlue,
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              <i className="bi bi-geo-alt-fill me-1 text-primary flex-shrink-0"></i> {project.district_name}
            </div>}
          </div>
        </div>
        <div className="d-flex w-100 gap-3 mb-3">
          {/* Budget Square Box - Takes half width */}
          {project.budget && (
            InfoItem({ icon: "bi bi-currency-dollar", value: project.budget === "R 0" ? "TBD" : project.budget })
          )}

          {/* Sector Square Box - Takes half width */}
          {project.score && (
            InfoItem({ icon: "bi bi-graph-up-arrow", value: `${project.score.toFixed(2)}%` })
          )}
        </div>
        <div className="mb-5">
          <GateStepper project_id={project.project_id} clickable stages={stages} size="sm" shape="square" />
          <div className="d-flex align-items-center">
            <hr className="flex-grow-1 text-primary" />
            <p className="fw-semibold px-3 mb-0 d-flex align-items-center text-gray-600 gap-2" >
              <i className="bi bi-diagram-3 me-1 text-gray-600 fw-semibold fs-6"></i>
              Stage Gates Progress
            </p>
            <hr className="flex-grow-1 text-primary" />
          </div>
        </div>
        <div className="mt-auto " style={{ borderColor: "#d6e8ff !important" }}>
          <Link
            to={`${getProjectEvaluationLink(project.project_id)}?return_to=${encodeURIComponent(window.location.pathname)}`}
            state={{ from: location.state?.from || location.pathname, }}
            className="text-decoration-none d-block"
          >
            <div className="stage-gate-card border-0 rounded-4 p-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3 min-w-0 flex-grow-1">
                <div
                  className="text-white rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "25px",
                    height: "25px",
                    background: gradients.iconBg,
                  }}
                >
                  <i className="bi bi-eye-fill fs-6 text-white"></i>
                </div>

                <div className="min-w-0">
                  <h6 className="fw-semibold mb-0 text-primary">
                    View Details
                  </h6>
                </div>
              </div>

              <div className="arrow-circle flex-shrink-0 ms-2">
                <i className="bi bi-chevron-right text-primary fs-5" ></i>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}

export { EnhancedProjectCard }